import { defineStore } from 'pinia';
import { usePodStore } from './PodStore.js';
import Api from '../api/Api';
import Helper from '../components/helper.js';

export const useSubtitleStore = defineStore('subtitle', {
  state: () => ({
    subtitles: [],
    origin: '',
    selectedAssetId: null,

    overlappingSubtitles: [], // Array of subtitle ID pairs that overlap
    overlapWarnings: [],      // Detailed warning messages
    allowOverlaps: true,      // User preference for overlap handling
  }),

  getters: {
    getSubtitleAssets: function() {
      const podStore = usePodStore();
      return podStore.getSubtitleAssets;
    },

    getSubtitles: function(state) {
      return state.subtitles;
    },

    getSelectedAsset: function(state) {
      return this.getSubtitleAssets.find((x) => x.id === state.selectedAssetId);
    },

    getTransformedSubtitles: function(state) {
      const cues = [];

      if (state.subtitles.length > 0) {
        for (let i = 0; i < state.subtitles.length; i++) {
          // Clean empty lines before transforming
          const cleanedText = Helper.cleanEmptyLines(state.subtitles[i].text);

          const cue = {
            type: 'cue',
            data: {
              text: cleanedText,
              start: state.subtitles[i].startTime,
              end: state.subtitles[i].endTime,
            },
          };

          cues.push(cue);
        }
      }

      return cues;
    },

    // New getters for overlap functionality
    hasOverlaps: (state) => state.overlappingSubtitles.length > 0,

    getOverlappingIds: (state) => {
      // Flatten all overlapping IDs into a Set
      const ids = new Set();
      state.overlappingSubtitles.forEach((pair) => {
        ids.add(pair[0]);
        ids.add(pair[1]);
      });
      return Array.from(ids);
    },

    isSubtitleOverlapping: (state) => (subtitleId) => {
      return state.overlappingSubtitles.some((pair) =>
        pair[0] === subtitleId || pair[1] === subtitleId,
      );
    },
  },

  actions: {
    setSubtitles: function(subtitles) {
      this.subtitles = subtitles;
      // Detect overlaps when subtitles are set
      this.detectOverlaps();
    },

    addSubtitleLine: function(payload) {
      const subtitles = [...this.subtitles];
      subtitles.push(payload.subtitle);

      subtitles.sort(function(a, b) {
        if (a.startTime < b.startTime) return -1;
        if (a.startTime > b.startTime) return 1;
        return 0;
      });

      for (let i = 0; i < subtitles.length; i++) {
        subtitles[i].id = i + 1;
      }

      this.subtitles = subtitles;
      // Detect overlaps after adding
      this.detectOverlaps();
    },

    updateSubtitleLine: function(payload) {
      if (this.subtitles.length > 0) {
        for (let i = 0; i < this.subtitles.length; i++) {
          if ((this.subtitles[i].id === payload.subtitle.id)) {
            this.subtitles[i].text = payload.subtitle.text;
            this.subtitles[i].endTime = payload.subtitle.endTime;
            this.subtitles[i].startTime = payload.subtitle.startTime;
            break;
          }
        }
        // Detect overlaps after updating
        this.detectOverlaps();
      }
    },

    removeSubtitleLine: function(payload) {
      if (this.subtitles.length > 0) {
        const foundItem = this.subtitles.find((item) => item.id === payload.subtitle.id);

        if (foundItem) {
          const filteredItems = this.subtitles.filter((item) => item.id !== payload.subtitle.id);
          this.subtitles = filteredItems;
          // Detect overlaps after removing
          this.detectOverlaps();
        }
      }
    },

    selectSubtitleAsset: async function(payload) {
      this.selectedAssetId = payload.assetId;

      const foundAsset = this.getSelectedAsset;
      if (!foundAsset) {
        return;
      }

      const result = await Api.subtitle.fetchContent(foundAsset.podid, foundAsset.id);
      this.setSubtitles(result.data.subtitles || []);
      this.origin = result.data.origin || '';
    },

    resetState() {
      this.subtitles = [];
      this.selectedAssetId = null;
      this.origin = '';
      // Reset overlap state
      this.overlappingSubtitles = [];
      this.overlapWarnings = [];
      this.allowOverlaps = false;
    },

    detectOverlaps() {
      const overlaps = [];
      const warnings = [];

      for (let i = 0; i < this.subtitles.length; i++) {
        for (let j = i + 1; j < this.subtitles.length; j++) {
          const sub1 = this.subtitles[i];
          const sub2 = this.subtitles[j];

          // Check if they overlap
          if (sub1.startTime < sub2.endTime && sub2.startTime < sub1.endTime) {
            overlaps.push([sub1.id, sub2.id]);

            const overlapStart = Math.max(sub1.startTime, sub2.startTime);
            const overlapEnd = Math.min(sub1.endTime, sub2.endTime);

            warnings.push({
              ids: [sub1.id, sub2.id],
              type: 'overlap',
              severity: 'warning',
              overlapDuration: overlapEnd - overlapStart,
              messageParams: {
                p1: sub1.id,
                p2: sub2.id,
              },
            });
          }
        }
      }

      this.overlappingSubtitles = overlaps;
      this.overlapWarnings = warnings;
    },

    setAllowOverlaps(allow) {
      this.allowOverlaps = allow;
    },

    splitOverlaps() {
      const sorted = [...this.subtitles].sort((a, b) => a.startTime - b.startTime);
      const MIN_DURATION = 100; // Minimum subtitle duration in milliseconds

      // First pass: fix any invalid subtitles (start >= end)
      for (let i = 0; i < sorted.length; i++) {
        if (sorted[i].startTime >= sorted[i].endTime) {
          // Set minimum duration for invalid subtitles
          sorted[i].endTime = sorted[i].startTime + MIN_DURATION;
        }
      }

      // Second pass: split overlaps
      for (let i = 1; i < sorted.length; i++) {
        const prev = sorted[i - 1];
        const curr = sorted[i];

        // Only process if there's a real overlap (not just sequential timing)
        if (prev.endTime > curr.startTime) {
          const overlap = prev.endTime - curr.startTime;
          const midPoint = curr.startTime + Math.floor(overlap / 2);

          // Ensure both subtitles maintain minimum duration
          const prevDuration = midPoint - prev.startTime;
          const currDuration = curr.endTime - midPoint;

          if (prevDuration >= MIN_DURATION && currDuration >= MIN_DURATION) {
            // Safe to split at midpoint
            prev.endTime = midPoint;
            curr.startTime = midPoint;
          } else {
            // Overlap is too small to split evenly, adjust differently
            if (overlap >= MIN_DURATION * 2) {
              // If total overlap allows, give each MIN_DURATION
              prev.endTime = curr.startTime;
              curr.startTime = prev.endTime;
            } else {
              // Keep the longer subtitle and shorten the other
              const prevTotalDuration = prev.endTime - prev.startTime;
              const currTotalDuration = curr.endTime - curr.startTime;

              if (prevTotalDuration > currTotalDuration) {
                prev.endTime = curr.startTime;
              } else {
                curr.startTime = prev.endTime;
              }
            }
          }
        }
      }

      this.subtitles = sorted;
      this.detectOverlaps();
    },

    mergeOverlaps() {
      const sorted = [...this.subtitles].sort((a, b) => a.startTime - b.startTime);
      const merged = [];
      let idCounter = 1;

      for (const subtitle of sorted) {
        const last = merged[merged.length - 1];

        if (last && last.endTime > subtitle.startTime) {
          // Merge with previous - combine texts and clean empty lines
          last.endTime = Math.max(last.endTime, subtitle.endTime);

          // Concatenate texts with newline
          const combinedText = `${last.text}\n${subtitle.text}`;

          // Clean empty lines from the merged result
          last.text = Helper.cleanEmptyLines(combinedText);
        } else {
          merged.push({
            ...subtitle,
            id: idCounter++,
          });
        }
      }

      this.subtitles = merged;
      this.detectOverlaps();
    },
  },
});
